import type { ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { Icon } from "@iconify/react";
import { MedSyncWordmark } from "@/shared/components/MedSyncWordmark";

/**
 * The split layout every auth screen uses, in all three portals (sign in,
 * sign up, forgot password, verification, reset password, invite screens):
 * logo pinned top-left, the form centered in a 455px column on the left, a
 * security note at the bottom, and the rounded photo panel on the right.
 *
 * Responsive: from lg (1024px, landscape tablets and laptops) up it's the
 * split view, the left column taking ~44% of the width; below lg (portrait
 * tablets) the photo is hidden and the form is centered on its own. A form
 * taller than the screen (sign up) scrolls inside the left column instead of
 * being clipped.
 *
 * Renders the route's <Outlet /> by default; a page that isn't mounted as a
 * child route (admin sign-in) passes its form as children instead.
 */
export function AuthFocusLayout({
  children,
  portalLabel = "Healthcare Platform",
}: {
  children?: ReactNode;
  /** Line under the wordmark, e.g. "Admin Portal". */
  portalLabel?: string;
}) {
  return (
    <div className="flex min-h-screen bg-[#FCFAFC] lg:h-screen lg:overflow-hidden">
      {/* ── Left column: logo · form · security note ── */}
      <div className="relative flex w-full flex-col lg:w-[44%] lg:shrink-0">
        <div className="relative flex shrink-0 items-center gap-2.5 px-4 pt-4 sm:px-5 sm:pt-5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white shadow-[0_1px_3px_rgba(17,24,39,0.08)]">
            <img src="/logo-icon.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
          </span>
          <div className="flex flex-col">
            <span className="text-base font-extrabold leading-tight tracking-[-0.02em] text-[#1B1340]">
              <MedSyncWordmark />
            </span>
            <span className="text-[13px] leading-tight text-[#6B7280]">{portalLabel}</span>
          </div>
        </div>

        {/* my-auto (not items-center) centers the form when it fits and lets a
            taller one scroll from its top instead of being cut off above. */}
        <main className="relative flex flex-1 flex-col overflow-y-auto px-6 py-10 sm:px-10 lg:pb-[9vh]">
          <div className="mx-auto my-auto w-full max-w-[455px]">
            {children ?? <Outlet />}
          </div>
        </main>

        <p className="relative flex shrink-0 items-center justify-center gap-1.5 px-6 pb-8 text-center text-xs text-[#6B7280] lg:pb-[13vh]">
          <Icon icon="lucide:lock" className="h-3 w-3 shrink-0" />
          Your health data is encrypted in transit and at rest.
        </p>
      </div>

      {/* ── Right column: photo (lg and up) ── */}
      <div className="hidden flex-1 py-3 pr-3 lg:flex">
        <div className="relative flex-1 overflow-hidden rounded-[36px]">
          <img
            src="/Image1.png"
            alt="A clinician reviewing health records with a patient on tablets"
            className="h-full w-full object-cover object-center"
          />
        </div>
      </div>
    </div>
  );
}
